import { useEffect, useState, useCallback } from 'react';
import { Button } from '@bn-apps/dim-ui-react';
import { useAuth } from '@hooks/useAuth';

const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 1 day
const WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before timeout

const SessionTimeout = (): JSX.Element | null => {
  const { user, login } = useAuth();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  const resetTimeout = useCallback((): void => {
    if (user?.isAuthenticated) {
      const timeoutTime = Date.now() + SESSION_TIMEOUT;
      localStorage.setItem('dim-session-timeout', timeoutTime.toString());
      setShowWarning(false);
    }
  }, [user?.isAuthenticated]);

  useEffect(() => {
    if (!user?.isAuthenticated) {
      return;
    }

    // Initialize or reset timeout on mount
    resetTimeout();

    // Activity listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimeout);
    });

    // Check timeout periodically
    const interval = setInterval(() => {
      const timeoutTime = parseInt(localStorage.getItem('dim-session-timeout') || '0');
      const remaining = timeoutTime - Date.now();

      if (remaining <= 0) {
        // Session expired locally - re-authenticate rather than fully signing
        // out of Azure AD, so oauth2-proxy sends the user back to this page
        // (login() reads window.location.href at call time).
        localStorage.removeItem('dim-session-timeout');
        login();
      } else if (remaining <= WARNING_TIME && !showWarning) {
        // Show warning
        setShowWarning(true);
        setTimeLeft(Math.ceil(remaining / 1000 / 60)); // Minutes
      } else if (remaining > WARNING_TIME && showWarning) {
        // User was active, hide warning
        setShowWarning(false);
      }
    }, 1000);

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimeout);
      });
      clearInterval(interval);
    };
  }, [user?.isAuthenticated, login, resetTimeout, showWarning]);

  if (!showWarning) {
    return null;
  }

  return (
    <div
      role="alertdialog"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-desc"
      style={{
        position: 'fixed',
        top: 'var(--dim-space-8)',
        right: 'var(--dim-space-8)',
        background: 'var(--dim-warning-solid)',
        color: 'var(--dim-on-warning-solid)',
        padding: 'var(--dim-space-6)',
        borderRadius: 'var(--dim-radius-floating)',
        boxShadow: 'var(--dim-shadow-floating)',
        zIndex: 'var(--dim-z-toast)',
        maxWidth: '400px',
      }}
    >
      <h3 id="session-timeout-title" style={{ marginBottom: 'var(--dim-space-2)' }}>
        Session Timeout Warning
      </h3>
      <p id="session-timeout-desc" style={{ marginBottom: 'var(--dim-space-4)' }}>
        Your session will expire in approximately {timeLeft} minute{timeLeft !== 1 ? 's' : ''}.
        Click anywhere on the page to stay signed in.
      </p>
      <Button
        onClick={resetTimeout}
        variant="primary"
        size="md"
      >
        Stay Signed In
      </Button>
    </div>
  );
};

export default SessionTimeout;
